import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Key, Smartphone, AlertTriangle, Check, Copy, 
  RefreshCw, Eye, EyeOff, Lock, AlertCircle, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function SecuritySettings() {
  // MFA State
  const [mfaStatus, setMfaStatus] = useState({ mfa_enabled: false, backup_codes_remaining: 0 });
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [loadingMfa, setLoadingMfa] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  
  // Password State
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Disable MFA state
  const [showDisableMfa, setShowDisableMfa] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  useEffect(() => {
    fetchSecurityStatus();
  }, []);

  const fetchSecurityStatus = async () => {
    try {
      const [mfaRes, passwordRes] = await Promise.all([
        api.get('/security/mfa/status'),
        api.get('/security/password/status')
      ]);
      setMfaStatus(mfaRes.data);
      setPasswordStatus(passwordRes.data);
    } catch (err) {
      console.error('Failed to fetch security status:', err);
    }
  };

  // ============================================================================
  // MFA Functions
  // ============================================================================

  const startMfaSetup = async () => {
    setLoadingMfa(true);
    try {
      const response = await api.post('/security/mfa/setup');
      setMfaSetupData(response.data);
      setShowMfaSetup(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setLoadingMfa(false);
    }
  };

  const verifyMfaSetup = async () => {
    if (!mfaCode || mfaCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoadingMfa(true);
    try {
      await api.post('/security/mfa/verify', { code: mfaCode });
      toast.success('MFA enabled successfully!');
      setShowMfaSetup(false);
      setMfaSetupData(null);
      setMfaCode('');
      fetchSecurityStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoadingMfa(false);
    }
  };

  const disableMfa = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoadingMfa(true);
    try {
      await api.post('/security/mfa/disable', { code: disableCode });
      toast.success('MFA has been disabled');
      setShowDisableMfa(false);
      setDisableCode('');
      fetchSecurityStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setLoadingMfa(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // ============================================================================
  // Password Functions
  // ============================================================================

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('One number');
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) errors.push('One special character');
    return errors;
  };

  const handleNewPasswordChange = (value) => {
    setNewPassword(value);
    setPasswordErrors(validatePassword(value));
  };

  const changePassword = async () => {
    if (passwordErrors.length > 0) {
      toast.error('Please fix password requirements');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoadingPassword(true);
    try {
      await api.post('/security/password/change', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully!');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors([]);
      fetchSecurityStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="security-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="h-7 w-7 text-[#4A238F]" />
            Security Settings
          </h1>
          <p className="text-slate-600 mt-1">Manage your account security and HIPAA compliance settings</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* MFA Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Smartphone className="h-5 w-5 text-[#4A238F]" />
                </div>
                <div>
                  <CardTitle className="text-lg">Two-Factor Authentication</CardTitle>
                  <CardDescription>Add an extra layer of security to your account</CardDescription>
                </div>
              </div>
              <Badge 
                variant={mfaStatus.mfa_enabled ? "default" : "secondary"}
                className={mfaStatus.mfa_enabled ? "bg-green-500" : ""}
              >
                {mfaStatus.mfa_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {mfaStatus.mfa_enabled ? (
              <>
                <Alert className="bg-green-50 border-green-200">
                  <Check className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Your account is protected with two-factor authentication.
                  </AlertDescription>
                </Alert>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Backup codes remaining:</span>
                  <Badge variant="outline">{mfaStatus.backup_codes_remaining}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowDisableMfa(true)}
                    data-testid="disable-mfa-btn"
                  >
                    Disable MFA
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    Two-factor authentication is not enabled. Enable it to secure your account.
                  </AlertDescription>
                </Alert>
                <Button 
                  onClick={startMfaSetup}
                  disabled={loadingMfa}
                  className="w-full bg-[#4A238F] hover:bg-[#3A1B72]"
                  data-testid="enable-mfa-btn"
                >
                  {loadingMfa ? 'Setting up...' : 'Enable Two-Factor Authentication'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Password Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Key className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Password</CardTitle>
                  <CardDescription>Manage your password and security policies</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {passwordStatus && (
              <>
                {passwordStatus.is_expired || passwordStatus.must_change ? (
                  <Alert className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      {passwordStatus.change_reason || 'Your password has expired. Please change it now.'}
                    </AlertDescription>
                  </Alert>
                ) : passwordStatus.warning ? (
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      Your password will expire in {passwordStatus.days_until_expiry} days.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="bg-green-50 border-green-200">
                    <Check className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      Password is valid. Expires in {passwordStatus.days_until_expiry} days.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="text-sm text-slate-600">
                  <p>Password policy: Changes required every {passwordStatus.expiry_days_policy} days</p>
                </div>
              </>
            )}
            <Button 
              onClick={() => setShowPasswordChange(true)}
              variant="outline"
              className="w-full"
              data-testid="change-password-btn"
            >
              <Lock className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* HIPAA Compliance Info */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-100">
              <Info className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <CardTitle className="text-lg">HIPAA Security Requirements</CardTitle>
              <CardDescription>Your account security status for compliance</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
              {mfaStatus.mfa_enabled ? (
                <Check className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              <div>
                <p className="font-medium text-slate-800">Multi-Factor Auth</p>
                <p className="text-xs text-slate-500">§164.312(d)</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
              {passwordStatus && !passwordStatus.is_expired ? (
                <Check className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              <div>
                <p className="font-medium text-slate-800">Password Policy</p>
                <p className="text-xs text-slate-500">§164.308(a)(5)</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
              <Check className="h-5 w-5 text-green-500" />
              <div>
                <p className="font-medium text-slate-800">Audit Logging</p>
                <p className="text-xs text-slate-500">§164.312(b)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* MFA Setup Dialog */}
      <Dialog open={showMfaSetup} onOpenChange={setShowMfaSetup}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          
          {mfaSetupData && (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center p-4 bg-white rounded-lg border">
                <img 
                  src={`data:image/png;base64,${mfaSetupData.qr_code}`}
                  alt="MFA QR Code"
                  className="w-48 h-48"
                />
              </div>
              
              {/* Manual entry key */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Can't scan? Enter this key manually:</Label>
                <div className="flex gap-2">
                  <Input 
                    value={mfaSetupData.manual_entry_key} 
                    readOnly 
                    className="font-mono text-sm"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(mfaSetupData.manual_entry_key)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Backup codes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-slate-600">Backup Codes (save these securely!):</Label>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowBackupCodes(!showBackupCodes)}
                  >
                    {showBackupCodes ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {showBackupCodes && (
                  <div className="grid grid-cols-2 gap-2 p-3 bg-slate-100 rounded-lg font-mono text-sm">
                    {mfaSetupData.backup_codes.map((code, i) => (
                      <div key={i} className="text-slate-700">{code}</div>
                    ))}
                  </div>
                )}
              </div>

              {/* Verification */}
              <div className="space-y-2">
                <Label>Enter code from your authenticator app:</Label>
                <div className="flex gap-2">
                  <Input 
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-lg font-mono tracking-widest"
                    maxLength={6}
                    data-testid="mfa-code-input"
                  />
                  <Button 
                    onClick={verifyMfaSetup}
                    disabled={loadingMfa || mfaCode.length !== 6}
                    className="bg-[#4A238F] hover:bg-[#3A1B72]"
                    data-testid="verify-mfa-btn"
                  >
                    {loadingMfa ? 'Verifying...' : 'Verify'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable MFA Dialog */}
      <Dialog open={showDisableMfa} onOpenChange={setShowDisableMfa}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Warning: This will make your account less secure. Enter your current MFA code to confirm.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                Disabling MFA reduces your account security and HIPAA compliance.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <Label>Enter your current MFA code:</Label>
              <Input 
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-lg font-mono tracking-widest"
                maxLength={6}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableMfa(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={disableMfa}
              disabled={loadingMfa || disableCode.length !== 6}
            >
              {loadingMfa ? 'Disabling...' : 'Disable MFA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new strong password.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <div className="relative">
                <Input 
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  data-testid="current-password-input"
                />
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="absolute right-0 top-0"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input 
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => handleNewPasswordChange(e.target.value)}
                  data-testid="new-password-input"
                />
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="absolute right-0 top-0"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              
              {/* Password requirements */}
              <div className="text-xs space-y-1">
                <p className={`flex items-center gap-1 ${newPassword.length >= 12 ? 'text-green-600' : 'text-slate-400'}`}>
                  {newPassword.length >= 12 ? <Check className="h-3 w-3" /> : <span className="w-3 h-3 rounded-full border" />}
                  At least 12 characters
                </p>
                <p className={`flex items-center gap-1 ${/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                  {/[A-Z]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3 h-3 rounded-full border" />}
                  One uppercase letter
                </p>
                <p className={`flex items-center gap-1 ${/[a-z]/.test(newPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                  {/[a-z]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3 h-3 rounded-full border" />}
                  One lowercase letter
                </p>
                <p className={`flex items-center gap-1 ${/[0-9]/.test(newPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                  {/[0-9]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3 h-3 rounded-full border" />}
                  One number
                </p>
                <p className={`flex items-center gap-1 ${/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                  {/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3 h-3 rounded-full border" />}
                  One special character
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input 
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="confirm-password-input"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={changePassword}
              disabled={loadingPassword || passwordErrors.length > 0 || newPassword !== confirmPassword || !currentPassword}
              className="bg-[#4A238F] hover:bg-[#3A1B72]"
              data-testid="submit-password-change-btn"
            >
              {loadingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
